<template>
  <div class="password-fields-group">
    <el-form-item class="passwords-field-group-form-item" label="New Password">
      <el-input
        :value="password"
        :type="showPassword ? 'text' : 'password'"
        @input="onPasswordInput"
        :clearable="false"
        autocomplete="new-password"
        name="new-password-field"
        class="password-input"
      >
        <img
          :src="eyeIcon(showPassword)"
          alt="toggle password visibility"
          slot="suffix"
          class="eye-icon"
          @click="showPassword = !showPassword"
        />
      </el-input>
    </el-form-item>

    <el-form-item class="passwords-field-group-form-item" label="Confirm Password">
      <el-input
        :value="confirmPassword"
        :type="showConfirmPassword ? 'text' : 'password'"
        @input="onConfirmPasswordInput"
        :clearable="false"
        autocomplete="new-password"
        name="confirm-password-field"
        class="password-input"
      >
        <img
          :src="eyeIcon(showConfirmPassword)"
          alt="toggle password visibility"
          slot="suffix"
          class="eye-icon"
          @click="showConfirmPassword = !showConfirmPassword"
        />
      </el-input>

      <div class="password-mismatch-error-slot">
        <p v-if="showMismatchError" class="password-mismatch-error">
          Password does not match. Try Again.
        </p>
      </div>
    </el-form-item>

    <div class="password-requirements-con">
      <p class="requirements-label">AT LEAST:</p>
      <div class="requirements-list">
        <p
          v-for="req in requirementsList"
          :key="req.key"
          class="requirement-chip"
          :class="requirementChipClass(req.key)"
        >
          <img
            v-if="passwordTouched"
            :src="requirementsStatus[req.key] ? '/img/icons/check-mark.svg' : '/img/icons/error-circle.svg'"
          />
          {{ req.label }}
      </p>
      </div>
    </div>
  </div>
</template>

<script>
import { FormItem, Input } from "element-ui";

const REQUIREMENTS_LIST = [
  { key: "length", label: "12 characters" },
  { key: "uppercase", label: "An uppercase letter" },
  { key: "number", label: "A number" },
  { key: "lowercase", label: "A lowercase letter" },
  { key: "special", label: "A special character" }
];

export default {
  name: "password-fields-group",

  components: {
    ElFormItem: FormItem,
    ElInput: Input
  },

  props: {
    password: {
      type: String,
      default: ""
    },
    confirmPassword: {
      type: String,
      default: ""
    },
  },

  data() {
    return {
      showPassword: false,
      showConfirmPassword: false,
      passwordTouched: false,
      mismatchTriggered: false,
      requirementsList: REQUIREMENTS_LIST
    };
  },

  computed: {
    requirementsStatus() {
      return {
        length: this.password.length >= 12,
        uppercase: /[A-Z]/.test(this.password),
        lowercase: /[a-z]/.test(this.password),
        number: /[0-9]/.test(this.password),
        special: /[^A-Za-z0-9]/.test(this.password)
      };
    },

    isPasswordValid() {
      return Object.values(this.requirementsStatus).every(Boolean);
    },

    passwordsMatch() {
      return this.password.length > 0 && this.password === this.confirmPassword;
    },

    isValid() {
      return this.isPasswordValid && this.passwordsMatch;
    },

    showMismatchError() {
      return this.mismatchTriggered && !this.passwordsMatch && this.confirmPassword.length > 0;
    }
  },

  watch: {
    isValid: {
      immediate: true,
      handler(newVal) {
        this.$emit("validity-change", {
          isValid: newVal,
          isPasswordValid: this.isPasswordValid,
          passwordsMatch: this.passwordsMatch
        });
      }
    }
  },

  methods: {
    eyeIcon(isVisible) {
      return isVisible ? "/img/icons/eye-icon-close.svg" : "/img/icons/eye-icon-open.svg";
    },
    requirementChipClass(key) {
      if (!this.passwordTouched) return "chip-neutral";
      return this.requirementsStatus[key] ? "chip-valid" : "chip-invalid";
    },

    onPasswordInput(value) {
      this.passwordTouched = true;
      if (this.confirmPassword.length > 0) {
        this.mismatchTriggered = true;
      }
      this.$emit("update:password", value);
    },

    onConfirmPasswordInput(value) {
      this.passwordTouched = true;
      this.mismatchTriggered = true;
      this.$emit("update:confirmPassword", value);
    },

    triggerMismatchCheck() {
      this.mismatchTriggered = true;
    },

    reset() {
      this.passwordTouched = false;
      this.mismatchTriggered = false;
      this.showPassword = false;
      this.showConfirmPassword = false;
    }
  }
};
</script>

<style scoped>
p {
    margin: 0;
}
.passwords-field-group-form-item {
  margin-bottom: 10px;
}
.password-input >>> .el-input__inner {
  height: 48px;
  line-height: 48px;
}

.passwords-field-group-form-item >>> .el-form-item__label {
  line-height: 2;
}

.password-input >>> .el-input__suffix {
  right: 12px;
  display: flex;
  align-items: center;
}

.eye-icon {
  width: 18px;
  height: 18px;
  margin-right: 6px;
  cursor: pointer;
}

.password-mismatch-error-slot {
  min-height: 20px;
  margin-top: 6px;
}

.password-mismatch-error {
  color: #D42620;
  font-size: 14px;
  margin: 0;
  line-height: 20px;
}

.password-requirements-con {
  margin: 0px 0 24px;
}

.requirements-label {
  display: block;
  font-size: 12px;
  letter-spacing: 0.05em;
  color: #475467;
  margin-bottom: 10px;
}

.requirements-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.requirement-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 400;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 12px;
}

.chip-neutral {
  background: #F7F9FC;
  color: #101928;
}

.chip-invalid {
  background: #FBEAE9;
  color: #D42620;
}

.chip-valid {
  background: #E7F6EC;
  color: #0F973D;
}
</style>